<template>
    <div class="slot-management-card card">
        <h2 class="card-title">Define My Daily Availability</h2>
        <p class="card-description">Select contiguous dates on the calendar to set your availability block.</p>
        
        <div class="calendar-controls">
            <button @click="changeMonth(-1)" class="month-btn">← Previous Month</button>
            <h3 class="current-month">{{ currentMonthName }} {{ currentYear }}</h3>
            <button @click="changeMonth(1)" class="month-btn">Next Month →</button>
        </div>

        <div class="calendar-grid">
            <div class="day-of-week" v-for="day in daysOfWeek" :key="day">{{ day }}</div>
            <div 
                v-for="date in calendarDays" 
                :key="date.fullDate || date.key" 
                :class="['calendar-day', { 
                    'is-filler': !date.isCurrentMonth,
                    'is-selected': isSelected(date.fullDate),
                    'is-today': isToday(date.fullDate),
                    'is-saved': isSaved(date.fullDate)
                }]"
                @click="date.isCurrentMonth ? handleDateClick(date.fullDate) : null"
            >
                {{ date.dayNumber }}
            </div>
        </div>
        
        <div class="bulk-actions">
            <button @click="selectAllDays" class="submit-btn bulk-btn">Select All {{ currentMonthName }}</button>
        </div>

        <hr class="divider">

        <div v-if="selectedDates.length > 0" class="time-selection-area">
            <h3 class="slot-list-title">Set Time for {{ selectedDates.length }} Selected Days</h3>

            <form @submit.prevent="generateSlots" class="slot-form">
                <div class="form-row">
                    <div class="form-group time-group">
                        <label for="bulk-start-time">Start Time</label>
                        <input type="time" id="bulk-start-time" v-model="bulkSlot.startTime" required>
                    </div>
                    <div class="form-group time-group">
                        <label for="bulk-stop-time">Stop Time</label>
                        <input type="time" id="bulk-stop-time" v-model="bulkSlot.stopTime" required>
                    </div>
                </div>
                <button type="submit" class="submit-btn add-btn">Generate & Preview Slots</button>
            </form>
        </div>

        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

        <div class="slot-list-container" v-if="availableSlots.length > 0">
            <h3 class="slot-list-title">Preview Slots for Submission ({{ availableSlots.length }})</h3>
            <ul class="slot-list">
                <li v-for="(slot, index) in availableSlots" :key="index" class="slot-item preview-item">
                    <div class="slot-details">
                        <span class="slot-date-display">{{ formatDate(slot.date) }}</span>
                        <span class="slot-time-display">{{ formatTime(slot.startTime) }} - {{ formatTime(slot.stopTime) }}</span>
                    </div>
                    <button @click="removeSlot(index)" class="remove-btn">✕</button>
                </li>
            </ul>

            <button @click="submitSlotsHandler" class="submit-btn final-submit-btn">
                Confirm & Submit {{ availableSlots.length }} Slots
            </button>
            <p v-if="backendMessage" class="backend-msg">{{ backendMessage }}</p>
        </div>

        <hr class="divider" v-if="savedSlots.length > 0">

        <div class="slot-list-container" v-if="savedSlots.length > 0">
            <h3 class="slot-list-title saved-slots-title">Confirmed Slots ({{ savedSlots.length }})</h3>
            <ul class="slot-list">
                <li v-for="(slot, index) in savedSlots" :key="index" class="slot-item saved-item">
                    <div class="slot-details">
                        <span class="slot-date-display">{{ formatDate(slot.date) }}</span>
                        <span class="slot-time-display">
                            {{ formatTime(slot.start_time || slot.startTime) }} - 
                            {{ formatTime(slot.end_time || slot.stop_time || slot.stopTime) }}
                        </span>
                    </div>
                    <button @click="deleteSlot(slot.id)" class="remove-btn delete-saved-btn">✕</button>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
//import axios from 'axios';
import { apiClient } from '@/services/ApiClient'

import axiosInstance from '@/services/AxiosClient';
import axios from 'axios';
const API_BASE_URL = process.env.VUE_APP_API_URL;

export default {
    name: 'ManageServiceSlots',
    props: {
        stylistId: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        const today = new Date();

        return {
            currentMonth: today.getMonth(),
            currentYear: today.getFullYear(),
            daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            selectedDates: [],
            bulkSlot: {
                startTime: '09:00',
                stopTime: '17:00'
            },
            availableSlots: [],
            savedSlots: [],
            errorMessage: null,
            backendMessage: null,
            // Centralized payload structure for backend submission
            slotPayload: {
                start: null,
                end: null,
                date: null,
                start_date: null,
                end_date: null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone 
            }
        };
    },
    computed: {
        currentMonthName() {
            return new Date(this.currentYear, this.currentMonth).toLocaleString('default', { month: 'long' });
        },
        calendarDays() {
            const days = [];
            const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
            const startingDay = firstDayOfMonth.getDay();
            const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

            // 1. Filler days (from previous month)
            const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
            for (let i = startingDay; i > 0; i--) {
                days.push({ 
                    dayNumber: prevMonthLastDay - i + 1, 
                    isCurrentMonth: false,
                    key: `prev-${i}` 
                });
            }
            // 2. Current month days
            for (let i = 1; i <= daysInMonth; i++) {
                const date = new Date(this.currentYear, this.currentMonth, i);
                days.push({
                    dayNumber: i,
                    isCurrentMonth: true,
                    fullDate: this.formatDateForStorage(date)
                });
            }
            const totalDaysToShow = 42; 
            const remainingDays = totalDaysToShow - days.length;
            for (let i = 1; i <= remainingDays; i++) {
                days.push({ 
                    dayNumber: i, 
                    isCurrentMonth: false,
                    key: `next-${i}` 
                });
            }

            return days;
        }
    },
    async created() {
        // Now calling the method directly from this.getSlots
        this.savedSlots = await this.getSlots(); 
        if(this.savedSlots.length > 0) {
            console.log("Slot: ", this.savedSlots[0]);
        }
    },
    methods: {
        changeMonth(delta) {
            this.availableSlots = []; 
            this.selectedDates = []; 
            this.errorMessage = null;
            let newMonth = this.currentMonth + delta;
            let newYear = this.currentYear;

            if (newMonth < 0) {
                newMonth = 11;
                newYear--;
            } else if (newMonth > 11) {
                newMonth = 0;
                newYear++;
            }
            this.currentMonth = newMonth;
            this.currentYear = newYear;
        },

        handleDateClick(dateString) {
            this.errorMessage = null;
            const index = this.selectedDates.indexOf(dateString);

            if (index !== -1) {
                this.selectedDates.splice(index, 1);
            } else {
                if (this.selectedDates.length > 0 && !this.isDateAdjacent(dateString)) {
                    this.errorMessage = 'Please select only **adjacent** days to form a continuous block.';
                    alert('⚠️ Non-Adjacent Date Selected! Please select only adjacent dates (the day before or the day after the current selection) to form a continuous block.');
                    return;
                }
                this.selectedDates.push(dateString);
                this.selectedDates.sort(); 
            }
            
            if (this.selectedDates.length > 2 && !this.isSelectionContiguous()) {
                this.errorMessage = 'Selection block was broken. Please select a contiguous range.';
                alert('⚠️ Selection block was broken. Please select a continuous range of dates.');
                this.selectedDates = [dateString]; 
            }
        },
        
        // This function checks if a newly clicked date is adjacent to the current selected block.
        isDateAdjacent(newDateString) {
            const dateMillis = new Date(newDateString).getTime();
            const dayMillis = 24 * 60 * 60 * 1000;
            
            const minSelectedMillis = new Date(this.selectedDates[0]).getTime();
            const maxSelectedMillis = new Date(this.selectedDates[this.selectedDates.length - 1]).getTime();

            const isAdjacentStart = dateMillis === minSelectedMillis - dayMillis;
            const isAdjacentEnd = dateMillis === maxSelectedMillis + dayMillis;
            
            return isAdjacentStart || isAdjacentEnd;
        },

        isSelectionContiguous() {
            if (this.selectedDates.length < 2) return true;
            
            for (let i = 1; i < this.selectedDates.length; i++) {
                const prevDate = new Date(this.selectedDates[i - 1]).getTime();
                const currDate = new Date(this.selectedDates[i]).getTime();
                const dayMillis = 24 * 60 * 60 * 1000;

                if (currDate - prevDate !== dayMillis) {
                    return false;
                }
            }
            return true;
        },

        isSelected(dateString) {
            return this.selectedDates.includes(dateString);
        },

        isToday(dateString) {
            if (!dateString) return false;
            const today = this.formatDateForStorage(new Date());
            return dateString === today;
        },

        isSaved(dateString) {
            // Check if this date has an already saved slot
            return this.savedSlots.some(slot => slot.date === dateString);
        },
        
        selectAllDays() {
            this.errorMessage = null;
            this.selectedDates = this.calendarDays
                .filter(day => day.isCurrentMonth)
                .map(day => day.fullDate)
                .sort();
        },

        // --- SLOT GENERATION AND PAYLOAD PREPARATION ---
        
        generateSlots() {
        if (this.selectedDates.length === 0) return;
        this.selectedDates.sort();

        // Create a basic list for the UI preview
        this.availableSlots = this.selectedDates.map(dateStr => ({
            date: dateStr,
            startTime: this.bulkSlot.startTime,
            stopTime: this.bulkSlot.stopTime
        }));
        
        this.errorMessage = null;
        this.backendMessage = null; 
    },/*
    async submitSlots() {
        if (this.availableSlots.length === 0) return;

        const first = this.availableSlots[0];
        const last = this.availableSlots[this.availableSlots.length - 1];

        // Format: "YYYY-MM-DD HH:MM:00"
        const startStr = `${first.date} ${this.bulkSlot.startTime}:00`;
        const endStr = `${first.date} ${this.bulkSlot.stopTime}:00`;

        const payload = {
            start: startStr,
            end: endStr,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Douala",
            // Single date if only one selected
            date: this.availableSlots.length === 1 ? first.date : null,
            // Range for multiple dates
            start_date: first.date,
            end_date: last.date
        };

        try {
            this.backendMessage = "Submitting...";
            const response = await axiosInstance.post(
                `/slot/create_slot/${this.stylistId}`, 
                payload
            );

            this.backendMessage = "Saved successfully!";
            this.savedSlots = await this.getSlots(); // Refresh list

            // Reset UI
            this.selectedDates = [];
            this.availableSlots = [];
            this.$emit('refresh-slots');

        } catch (error) {
            const errorMsg = error.response?.data?.error || "Server Error (500)";
            console.error("Submission Error:", error.response?.data);
            this.backendMessage = `Error: ${errorMsg}`;
            alert(`Submission failed: ${errorMsg}`);
        }
    },*/
    async submitSlots() {
    if (this.availableSlots.length === 0) return;

    // 1. Get the local timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 2. Use the date from the previewed slots
    const first = this.availableSlots[0];
    const last = this.availableSlots[this.availableSlots.length - 1];

    try {
        const payload = {
            // Use 'first.date' instead of 'this.bulkSlot.date'
            start: new Date(`${first.date}T${this.bulkSlot.startTime}`).toISOString(),
            end: new Date(`${first.date}T${this.bulkSlot.stopTime}`).toISOString(),
            
            timezone: userTimezone, 
            start_date: first.date, 
            end_date: last.date
        };

        this.backendMessage = "Submitting...";
        const response = await axiosInstance.post(`/slot/create_slot/${this.stylistId}`, payload);
        
        this.backendMessage = "Saved successfully!";
        
        // Refresh and Reset
        const updated = await this.getSlots();
        this.savedSlots = updated;
        this.selectedDates = [];
        this.availableSlots = [];
        this.$emit('refresh-slots');

    } catch (error) {
        console.error("Submission Error:", error);
        this.backendMessage = `Error: ${error.response?.data?.error || 'Failed to save'}`;
    }

},
        
        removeSlot(index) {
            this.availableSlots.splice(index, 1);
            this.preparePayload();
        },

        // This function prepares the final payload based on the availableSlots list
        preparePayload() {
            if (this.availableSlots.length === 0) {
                // Reset payload if no slots are available
                this.slotPayload = { timezone: this.slotPayload.timezone };
                return;
            }

            this.availableSlots.sort((a, b) => new Date(a.date) - new Date(b.date));

            const isSingleRange = this.isSelectionContiguous() && 
                                  this.availableSlots.every(s => s.startTime === this.bulkSlot.startTime && s.stopTime === this.bulkSlot.stopTime);

            const firstSlot = this.availableSlots[0];
            const lastSlot = this.availableSlots[this.availableSlots.length - 1];

            // Reset payload and fill based on single vs. range
            this.slotPayload = { timezone: this.slotPayload.timezone }; 

            // Case 1: Multiple contiguous days with same time (Use range parameters)
            if (isSingleRange && this.availableSlots.length > 1) {
                this.slotPayload.start = `${firstSlot.date}T${firstSlot.startTime}:00`; 
                this.slotPayload.end = `${firstSlot.date}T${firstSlot.stopTime}:00`;   
                this.slotPayload.start_date = firstSlot.date;
                this.slotPayload.end_date = lastSlot.date;
            } 
            // Case 2: Single day submission 
            else if (this.availableSlots.length === 1) {
                 this.slotPayload.start = `${firstSlot.date}T${firstSlot.startTime}:00`; 
                 this.slotPayload.end = `${firstSlot.date}T${firstSlot.stopTime}:00`;  
                 this.slotPayload.date = firstSlot.date;
            } else {
                 // Non-contiguous or empty multi-select submission (Should be rejected)
                 this.errorMessage = "Warning: The selected slots are non-contiguous. Only single days or continuous blocks are supported for submission."
            }
        },
       
        // Entry point for submission
        submitSlotsHandler() {
            // Final check before submission
            if (this.availableSlots.length === 0) {
                this.backendMessage = 'Please generate slots before submitting.';
                return;
            }
            this.submitSlots(); 
        },

        async deleteSlot(slotId) {
            /** Deletes a specific slot by ID and updates the savedSlots list. **/
            this.backendMessage = 'Deleting slot...';
            console.log(`Slot ID is: ${slotId} while the stylist ID is: ${this.stylistId}`)

            try {
                // API call to delete the slot using the stylist ID and slot ID
                // await apiClient(`/slot/delete_slot/${this.stylistId}/${slotId}`, {
                    // method: 'DELETE'
                // });
                const response = await axiosInstance.delete(`/slot/delete_slot/${this.stylistId}/${slotId}`)
                console.log('Delete successful: ', response.data.message)

                // On success, filter the slot out of the savedSlots array for immediate UI update
                this.savedSlots = this.savedSlots.filter(slot => slot.id !== slotId);

                this.backendMessage = 'Slot successfully deleted!';
                this.$emit('refresh-slots')

            } catch (error) {
                let errorMsg = 'Failed to delete slot.';
                if (typeof error === 'string') {
                    errorMsg = error;
                } else if (error && error.error) {
                    errorMsg = error.error;
                }
                console.error("Failed to delete slot:", error);
                this.backendMessage = `Deletion Failed: ${errorMsg}`;

            } finally {
                setTimeout(() => { this.backendMessage = null; }, 3000);
            }
        },

        
        async getSlots() {
            // Fetches existing slots from the backend for the current stylist. 
            try {
                this.errorMessage = null
                const response = await axios.get(`${API_BASE_URL}/slot/get_stylist_slots/${this.stylistId}`)
                return response.data
            }catch (error){
                console.log("The operation failed because of this ", error)
                this.errorMessage = "Failed to Load slots, Session might be expired"
                return []
            }
        },
        //                    UTILITY FORMATTERS
       
        formatDateForStorage(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
       
        formatDate(dateString) {
    if (!dateString) return 'N/A';
    
    // Parse the date string coming from the backend
    const date = new Date(dateString);
    
    // If parsing fails, return the original string as a fallback
    if (isNaN(date.getTime())) return dateString;

    // This returns ONLY the date part (e.g., "Sun, Mar 1, 2026")
    return date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
},
formatTime(timeValue) {
    if (!timeValue) return '--:--';

    // Parse the time/date string
    const date = new Date(timeValue);

    // If it's a valid date object (ISO or GMT string), format the time
    if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
        });
    }

    // Fallback for simple "HH:MM" strings from the "Preview" state
    const [hours, minutes] = String(timeValue).split(':');
    const dummyDate = new Date();
    dummyDate.setHours(parseInt(hours), parseInt(minutes));
    return dummyDate.toLocaleTimeString([], { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
    });
}

    }
};
</script>

<style scoped>

.slot-management-card {
    background-color: white;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-left: 5px solid var(--color-maroon); 
}

.card-title {
    font-family: var(--font-serif);
    font-size: 1.8rem;
    color: var(--color-maroon);
    margin-bottom: 0.75rem;
    border-bottom: 2px solid var(--color-gold);
    padding-bottom: 5px;
}

.card-description {
    color: #4b5563; 
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
}

/* Calendar Styles */
.calendar-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background-color: var(--color-maroon);
    color: white;
    padding: 10px 15px;
    border-radius: 8px;
}

.current-month {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: var(--color-gold);
}

.month-btn {
    background: none;
    border: none;
    color: white;
    padding: 8px 12px;
    cursor: pointer;
    font-weight: 600;
    transition: color 0.2s;
}

.month-btn:hover {
    color: var(--color-gold);
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 5px;
    text-align: center;
}

.day-of-week {
    font-weight: 700;
    color: var(--color-earth);
    padding: 10px 0;
}

.calendar-day {
    padding: 10px 5px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;
    font-weight: 500;
    color: var(--color-dark);
    border: 1px solid transparent;
    position: relative; /* For the dot */
}

.calendar-day:hover:not(.is-filler):not(.is-disabled) {
    background-color: #f0f0f0;
    transform: scale(1.05);
}

.is-filler, .is-disabled {
    color: #ccc;
    cursor: default;
    background-color: #fafafa;
}

.is-selected {
    background-color: var(--color-gold);
    color: var(--color-dark);
    font-weight: 700;
    border: 1px solid var(--color-earth);
    box-shadow: 0 0 5px rgba(251, 191, 36, 0.5);
}

.is-today {
    border: 2px solid var(--color-maroon);
    background-color: #fefcf5;
}

/* Styling for Saved Slots on Calendar */
.is-saved {
    background-color: #e6ffe6; /* Light green to indicate slot is saved */
    border: 1px dashed green;
    position: relative;
}
/* indicator dot for elegance */
.is-saved::after {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: green;
}


/* Bulk Actions */
.bulk-actions {
    margin-top: 20px;
    text-align: center;
}

.bulk-btn {
    background-color: #A0522D;  /**** */
    width: 100%;
    margin-top: 0;
    padding: 10px;
    font-size: 20px;
    font-family: 'Times New Roman', Times, serif;
    color: #f5f5f4;
}

.bulk-btn:hover {
    background-color: #8d4523;
}

.divider {
    margin: 30px 0;
    border: 0;
    height: 1px;
    background-image: linear-gradient(to right, rgba(93, 14, 31, 0), rgba(93, 14, 31, 0.75), rgba(93, 14, 31, 0));
}

/* Time Selection Area */
.time-selection-area {
    margin-bottom: 2rem;
}

.slot-form {
    padding-top: 1.5rem;
}

.form-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-bottom: 1rem;
    justify-content: flex-start;
}

.form-group {
    flex: 0 1 180px; 
}

.form-group label {
    display: block;
    font-weight: 600;
    color: var(--color-earth);
    margin-bottom: 0.4rem;
    font-size: 0.9rem;
}

.form-group input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    box-sizing: border-box;
    transition: border-color 0.3s;
}

.add-btn {
    margin-top: 1rem;
    width: auto;
    max-width: 300px;
}

/* Slot List  */
.slot-list-container {
    margin-top: 1.5rem;
}

.slot-list-title {
    font-size: 1.2rem;
    color: var(--color-dark);
    margin-bottom: 1rem;
    font-weight: 600;
    font-family: var(--font-serif);
}

.saved-slots-title {
    color: var(--color-earth);
    border-bottom: 2px dashed var(--color-earth);
    padding-bottom: 5px;
}

.slot-list {
    list-style: none;
    padding: 0;
    max-height: 250px;
    overflow-y: auto;
}

.slot-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    margin-bottom: 8px;
    border-radius: 6px;
}

.preview-item {
    background-color: var(--color-light);
    border: 1px solid var(--color-maroon);
}

.saved-item {
    background-color: #e6ffe6;
    border: 1px solid green;
    opacity: 0.9;
}

.slot-date-display {
    font-weight: 600;
    color: var(--color-maroon);
    margin-right: 15px;
}

.slot-time-display {
    color: var(--color-dark);
}

.remove-btn {
    background: none;
    border: none;
    color: #e74c3c;
    font-weight: 700;
    cursor: pointer;
    transition: color 0.2s;
}

.remove-btn:hover {
    color: var(--color-maroon);
}

 .submit-btn { 
    background-color: #A0522D;  /**** */
    width: 100%;
    margin-top: 0;
    padding: 10px;
    font-size: 20px;
    font-family: 'Times New Roman', Times, serif;
    color: #f5f5f4;
}

.final-submit-btn {
    margin-top: 2rem;
    width: 100%;
    background-color: var(--color-maroon);
}

.error-message {
    color: var(--color-maroon);
    background-color: #fff0f0;
    border: 1px solid var(--color-maroon);
    padding: 10px;
    border-radius: 6px;
    text-align: center;
    margin-bottom: 15px;
}

.backend-msg {
    text-align: center;
    margin-top: 15px;
    padding: 10px;
    background-color: #e6ffe6;
    border: 1px solid green;
    color: green;
    border-radius: 6px;
}

@media (max-width: 600px) {
    .slot-management-card {
        padding: 1rem;       /* Reduce from 2.5rem to 1rem for more space */
    }

    .calendar-grid {
        gap: 2px;            /* Tighter gap for small screens */
    }

    .calendar-day {
        padding: 8px 2px;    /* Slightly smaller touch targets */
        font-size: 0.85rem;  /* Reduce font size slightly */
    }

    .day-of-week {
        font-size: 0.8rem;   /* Smaller weekday labels */
    }

    .current-month {
        font-size: 1.2rem;   /* Smaller month title */
    }
    
    .form-row {
        flex-direction: column; /* Stack start/stop time inputs vertically */
        gap: 10px;
    }
    
    .form-group {
        flex: 1 1 100%;         /* Ensure inputs take full width */
    }
}
</style>